import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { setMinutes } from "date-fns/setMinutes";
import { setHours } from "date-fns/setHours";
import { de as LocaleDe } from "date-fns/locale/de";
import { fr as LocaleFr } from "date-fns/locale/fr";
import { es as LocaleEs } from "date-fns/locale/es";
import { it as LocaleIt } from "date-fns/locale/it";
import { nl as LocaleNl } from "date-fns/locale/nl";
import { enUS as LocaleEn } from "date-fns/locale/en-US";
import { Theme } from "../../../..";
/**
 * @todo: When this component moves layers, these imports shoud be updated
 * to their package aliases (i.e. '@silvertours/front-legacy-shared').
 */ import { useLocale } from "../../../I18n";
import { dateFormats, isoDateTime } from "../../../../strings";
import { ErrorMessage } from "../../../../Ui/ErrorMessage";
import { DatePickerButton, DatePicker } from "./DatePicker";
import { TimePicker } from "./TimePicker";
import { Backdrop, TimeRange, DatePickerWrapper, StyledSubmitButton } from "./DateRangePicker.styles";
var HOUR_IN_MS = 1000 * 60 * 60;
var INITIAL_MONTHS_MOBILE_CALENDAR = 6;
var MAXIMUM_MONTHS_IN_CALENDAR = 24;
export var addTimeToDate = function(date, hours, minutes) {
    return setMinutes(setHours(date, hours), minutes);
};
/** @todo: Move to /entities layer - this is too sophisticated for /shared */ var DateRangePicker = function(param) {
    var from = param.from, to = param.to, _open = param.open, open = _open === void 0 ? false : _open, _vertical = param.vertical, vertical = _vertical === void 0 ? false : _vertical, size = param.size, error = param.error, onDatesChange = param.onDatesChange;
    /**
   * @todo:
   * The translation key should be aligned with the current layer/slice (i.e.
   * 'entities.dateLegacy.dateRangePicker' - once the component has been moved
   * to the /entities layer)
   * */ var t = useTranslations("features.stageLegacy.stage.searchForm");
    var language = useLocale().language;
    var dateLocale = {
        de: LocaleDe,
        fr: LocaleFr,
        es: LocaleEs,
        it: LocaleIt,
        nl: LocaleNl,
        en: LocaleEn
    }[language];
    var ref = useState(open), isCalendarVisible = ref[0], setCalendarVisibility = ref[1];
    var ref1 = useState({
        from: from,
        to: to
    }), selectedDates = ref1[0], setSelectedDates = ref1[1];
    var isMobileTouchDevice = Theme.useIsMobileTouchDevice();
    useEffect(function() {
        setSelectedDates({
            from: from,
            to: to
        });
    }, [
        from,
        to
    ]);
    var localDateFormats = dateFormats[language];
    var handleBackdropClick = function(event) {
        event.stopPropagation();
        setCalendarVisibility(false);
    };
    var handleOnDatesChange = function(dateRange) {
        var newDateRange = {
            from: new Date(),
            to: new Date()
        };
        if (dateRange.from && dateRange.to && selectedDates.from && selectedDates.to) {
            newDateRange = {
                from: addTimeToDate(dateRange.from, selectedDates.from.getHours(), selectedDates.from.getMinutes()),
                to: addTimeToDate(dateRange.to, selectedDates.to.getHours(), selectedDates.to.getMinutes())
            };
            if (format(dateRange.from, isoDateTime).split("T")[0] === format(dateRange.to, isoDateTime).split("T")[0]) {
                newDateRange = {
                    from: addTimeToDate(dateRange.from, 10, 0),
                    to: addTimeToDate(dateRange.to, 17, 0)
                };
            }
            if (typeof onDatesChange !== "undefined") {
                onDatesChange(newDateRange);
            }
            setSelectedDates(newDateRange);
            // keep datepicker on mobile visible after date selection
            setCalendarVisibility(isMobileTouchDevice);
        }
    };
    var handleOnTimeChange = function(time, type) {
        var ref = _sliced_to_array(time.split(":"), 2), hours = ref[0], minutes = ref[1];
        if (selectedDates[type]) {
            var dateWithTime = addTimeToDate(selectedDates[type], Number(hours), Number(minutes));
            var newDateRange = _object_spread_props(_object_spread({}, selectedDates), _define_property({}, type, dateWithTime));
            if (typeof onDatesChange !== "undefined") {
                onDatesChange(newDateRange);
            }
            setSelectedDates(newDateRange);
        }
    };
    var getDays = function() {
        if (selectedDates.from && selectedDates.to) {
            // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
            // because differenceInHours() from date-fns always rounds which is not what we want
            var days = (selectedDates.to - selectedDates.from) / HOUR_IN_MS / 24;
            return Number.isInteger(days) ? days : Math.ceil(days);
        }
        return 1;
    };
    var getDefaultMonth = function() {
        if (vertical || !selectedDates.from) {
            return undefined;
        }
        return selectedDates.from;
    };
    var getInitialNumberOfMonths = function() {
        if (vertical) {
            return INITIAL_MONTHS_MOBILE_CALENDAR;
        }
        return size === "small" ? 1 : 2;
    };
    var ref2 = useState(getInitialNumberOfMonths()), numberOfMonths = ref2[0], setNumberOfMonths = ref2[1];
    var addMoreMonths = useCallback(function() {
        setNumberOfMonths(Math.min(numberOfMonths + 4, MAXIMUM_MONTHS_IN_CALENDAR));
    }, [
        numberOfMonths
    ]);
    useEffect(function() {
        // Reset months on viewport change
        setNumberOfMonths(getInitialNumberOfMonths());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        vertical,
        size
    ]);
    var now = new Date();
    var calendarRange = {
        start: now,
        end: addMonths(now, MAXIMUM_MONTHS_IN_CALENDAR)
    };
    var dayCountText = t("dayCount", {
        days: getDays()
    });
    var handleButtonClick = useCallback(function() {
        setCalendarVisibility(!isCalendarVisible);
    }, [
        isCalendarVisible
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isCalendarVisible && // OnKeyDown is necessary for accessibility reasons
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            /*#__PURE__*/ _jsx(Backdrop, {
                onClick: handleBackdropClick
            }),
            /*#__PURE__*/ _jsx(DatePickerButton, {
                title: t("datePicker"),
                from: from,
                to: to,
                size: size,
                onClick: handleButtonClick
            }),
            /*#__PURE__*/ _jsx(ErrorMessage, {
                children: error
            }),
            isCalendarVisible && /*#__PURE__*/ _jsxs(DatePickerWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(DatePicker, {
                        onDatesChange: handleOnDatesChange,
                        onClose: function() {
                            setCalendarVisibility(false);
                        },
                        dayCountText: dayCountText,
                        disabled: {
                            before: new Date()
                        },
                        numberOfMonths: numberOfMonths,
                        fromMonth: calendarRange.start,
                        toMonth: calendarRange.end,
                        from: selectedDates.from ? selectedDates.from : undefined,
                        to: selectedDates.to ? selectedDates.to : undefined,
                        locale: dateLocale,
                        defaultMonth: getDefaultMonth(),
                        scrollToSelectedMonth: vertical,
                        dateFormat: vertical ? localDateFormats.dayDateLongYear : localDateFormats.dayDate,
                        size: size,
                        onScrollToBottom: addMoreMonths,
                        vertical: vertical
                    }),
                    size !== "small" && /*#__PURE__*/ _jsx(StyledSubmitButton, {
                        type: "button",
                        onClick: function() {
                            return setCalendarVisibility(false);
                        },
                        children: dayCountText
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(TimeRange, {
                children: [
                    /*#__PURE__*/ _jsx(TimePicker, {
                        id: "depTime",
                        type: "from",
                        label: t("depTime.label"),
                        value: selectedDates.from ? format(selectedDates.from, localDateFormats.timeGeneric) : undefined,
                        onChange: handleOnTimeChange
                    }),
                    /*#__PURE__*/ _jsx(TimePicker, {
                        id: "destTime",
                        type: "to",
                        label: t("destTime.label"),
                        value: selectedDates.to ? format(selectedDates.to, localDateFormats.timeGeneric) : undefined,
                        onChange: handleOnTimeChange
                    })
                ]
            })
        ]
    });
};
export { DateRangePicker };
